import React, { useEffect, useState, useRef } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useMutation, useLazyQuery } from '@apollo/client'
import moment from 'moment'

import { useCartRedux, useOrderRedux } from '@hooks'
import { ROUTES, customToast } from '@common'
import { Loader, OrderSummaryTotals } from '@components'
import { CREATE_PAYMENT_INTENT, CHECK_ORDER_PAYMENT_STATUS, ErrorTrackingAPI } from '@services'
import { storageHelpers, logger } from '@utils'

export default function OrderCompletePage() {
    const { orderId } = useParams()
    const navigate = useNavigate()
    const { completeOrder, setCompleteOrder, setSearchedOrder, fetchOrderById } = useOrderRedux()
    const { clearCart } = useCartRedux()
    // eslint-disable-next-line
    const [isCopied, setIsCopied] = useState(false)
    const hasCheckedStatusOnMount = useRef(false)
    const hasShownPromoRejections = useRef(false)

    const [createPaymentIntent, { loading: isGeneratingQr }] = useMutation(CREATE_PAYMENT_INTENT, {
        onCompleted: data => {
            if (data?.createPaymentIntent?.result) {
                const { id: paymentIntentId, client_secret: clientSecret, next_action } = data.createPaymentIntent.paymentIntent
                const qrCodeSVG = next_action?.paynow_display_qr_code?.image_url_svg
                const hostedVoucherUrl = next_action?.paynow_display_qr_code?.hosted_instructions_url
                if (completeOrder?.pickupScheduledAt) {
                    storageHelpers.setPaymentOrigin('advance-takeaway')
                } else {
                    storageHelpers.setPaymentOrigin('regular-order')
                }
                navigate(
                    ROUTES.Payment.root
                        .replace(':orderId', completeOrder.orderId)
                        .replace(':paymentIntentId', paymentIntentId)
                        .replace(':clientSecret', clientSecret),
                    { state: { qrCodeSVG, hostedVoucherUrl } }
                )
            } else {
                customToast.error(data?.createPaymentIntent?.error || 'Failed to generate PayNow QR')
            }
        },
        onError: err => {
            ErrorTrackingAPI.sendError(err, 'OrderCompletePage', 'createPaymentIntent')
            customToast.error(err?.message || 'Failed to generate PayNow QR')
        },
    })

    const [checkOrderStatus, { loading: isCheckingStatus }] = useLazyQuery(CHECK_ORDER_PAYMENT_STATUS, {
        onCompleted: data => {
            if (data?.checkOrderPaymentStatus?.result) {
                const order = data.checkOrderPaymentStatus.order

                // Update order state
                const mergedOrder = { ...completeOrder, ...order, promoResult: completeOrder?.promoResult || order?.promoResult }
                setCompleteOrder(mergedOrder)
                setSearchedOrder(mergedOrder)

                // Only navigate if payment is confirmed and it's a preorder
                // For regular orders, stay on this page to show updated status
                if (order.payment?.paidAt && order.pickupScheduledAt) {
                    // If order is a preorder (has pickupScheduledAt), navigate to member history with upcoming tab
                    navigate(`${ROUTES.Order.history}?tab=upcoming`, { replace: true })
                    return
                }

                // For manual check (user clicks button), navigate to order status page
                // For automatic check on mount, stay on this page
                if (!hasCheckedStatusOnMount.current) {
                    navigate(ROUTES.Order.number.replace(':orderNumber', order.number))
                }
            } else {
                customToast.error(data?.checkOrderPaymentStatus?.error || 'Failed to check payment status')
            }
        },
        onError: err => {
            ErrorTrackingAPI.sendError(err, 'OrderCompletePage', 'checkPaymentStatus')
            customToast.error(err?.message || 'Failed to check payment status')
        },
        fetchPolicy: 'network-only',
    })

    useEffect(() => {
        clearCart()
        // Scroll to top when page loads
        window.scrollTo({ top: 0, behavior: 'smooth' })
        // eslint-disable-next-line
    }, [])

    useEffect(() => {
        if (completeOrder === null) {
            const fetchOrderByIdAsync = async () => {
                const res = await fetchOrderById(orderId)
                if (!res.payload) {
                    navigate('/not-found', { replace: true })
                }
            }

            fetchOrderByIdAsync()
        }
    }, [completeOrder, orderId, fetchOrderById, navigate])

    // Auto-check order status when page loads to update payment status
    useEffect(() => {
        if (completeOrder && !hasCheckedStatusOnMount.current) {
            hasCheckedStatusOnMount.current = true
            checkOrderStatus({
                variables: {
                    orderId: completeOrder.orderId,
                },
            })
        }
    }, [completeOrder, checkOrderStatus])

    useEffect(() => {
        const rejectedPromos = completeOrder?.promoResult?.rejectedPromos || []
        if (rejectedPromos.length === 0 || hasShownPromoRejections.current) return
        hasShownPromoRejections.current = true
        rejectedPromos.forEach(promo => {
            customToast.error(promo?.message || `${promo?.displayName || 'Promo'} was not applied`)
        })
    }, [completeOrder])

    const onGeneratePaynowQr = async () => {
        await createPaymentIntent({
            variables: {
                input: {
                    orderId: completeOrder.orderId,
                    orderNumber: completeOrder.number,
                    amount: completeOrder.summary?.finalAmount || 0,
                },
            },
        })
    }

    const onCheckOrderStatus = () => {
        // Reset flag so navigation happens on manual check
        hasCheckedStatusOnMount.current = false
        checkOrderStatus({
            variables: {
                orderId: completeOrder.orderId,
            },
        })
    }

    // Handle copy order number to clipboard
    // eslint-disable-next-line
    const handleCopyOrderNumber = async () => {
        try {
            const orderNumber = completeOrder.orderId || completeOrder.number || ''
            await navigator.clipboard.writeText(orderNumber)
            setIsCopied(true)
            setTimeout(() => {
                setIsCopied(false)
            }, 2000)
        } catch (err) {
            logger.error('Failed to copy order number:', err)
        }
    }

    // Format time for display (e.g., "06:27 PM")
    const formatPlacedTime = dateString => {
        if (!dateString) return ''
        return moment(dateString).format('hh:mm A')
    }

    // Calculate dish total with selections
    const calculateDishTotal = dish => {
        let total = 0
        if (dish.variants && dish.variants.length > 0) {
            dish.variants.forEach(variant => {
                const variantTotal = variant.sellPrice * variant.quantity
                total += variantTotal

                // Add selections cost
                if (dish.selections && dish.selections.length > 0) {
                    dish.selections.forEach(selection => {
                        if (selection.options && selection.options.length > 0) {
                            selection.options.forEach(option => {
                                total += option.sellPrice * option.quantity * variant.quantity
                            })
                        }
                    })
                }
            })
        }
        return total
    }

    // Get simplified dish name for display
    const getDishDisplayName = dish => {
        if (!dish || !dish.variants || dish.variants.length === 0) return dish?.name || 'Unknown'

        // If only one variant and it's "Default Title", just show dish name
        if (dish.variants.length === 1 && dish.variants[0]?.name === 'Default Title') {
            return dish.name
        }

        // Otherwise, show dish name with variant name
        return dish.name
    }

    // Calculate total quantity for a dish
    const getDishQuantity = dish => {
        if (!dish || !dish.variants) return 1
        return dish.variants.reduce((total, variant) => total + (variant.quantity || 1), 0)
    }

    if (!completeOrder || isGeneratingQr || isCheckingStatus) {
        return <Loader />
    }

    const finalAmount = completeOrder.summary?.finalAmount || 0
    const originalAmount = completeOrder.summary?.originalAmount || 0
    const benefitDiscount = completeOrder.summary?.benefitDiscount || 0
    const creditDiscount = completeOrder.summary?.creditDiscount || 0
    const promoDiscount = completeOrder.summary?.promoDiscount || 0
    const isPaid = !!completeOrder.payment?.paidAt || completeOrder.status === 'PAID' || finalAmount === 0

    return (
        <div className="order-complete-page">
            {/* Header */}
            <div className="order-complete-header">
                <p className="order-complete-title">Thank you for placing the order</p>
                <h5 className="order-number-label">Order number</h5>
                <div className="order-number-container">
                    <h1 className="order-number">{completeOrder.number}</h1>
                    {/* <div className="copy-order-number-btn" onClick={handleCopyOrderNumber}>
                        <i className={isCopied ? 'bx bx-check' : 'bx bx-copy'}></i>
                        {isCopied && <span className="copy-feedback">Copied!</span>}
                    </div> */}
                </div>
            </div>

            {/* Warning Box */}
            <div className="order-warning-box">Do not close this page yet. Your order will only be confirmed after payment.</div>

            {/* Instructions */}
            <div className="order-instructions">
                <div className="instruction-item">
                    <div className="instruction-number">1</div>
                    <p className="instruction-text">Pay via Paynow or proceed to drink stall cashier for cash payment</p>
                </div>
                <div className="instruction-item">
                    <div className="instruction-number">2</div>
                    <p className="instruction-text">Check your order status</p>
                </div>
                <div className="instruction-item">
                    <div className="instruction-number">3</div>
                    <p className="instruction-text">
                        Self-collect your food at the respective stall(s) when it's ready for pickup
                    </p>
                </div>
            </div>

            {/* Pay via PayNow Section */}
            {!isPaid && (
                <div className="order-paynow-card">
                    <div className="paynow-header">
                        <h5 className="paynow-title">Pay via PayNow</h5>
                        <span className="paynow-total">Total $ {finalAmount.toFixed(2)}</span>
                    </div>
                    <p className="paynow-description">
                        <span>Generate a QR code and upload the screenshot to your banking app.</span>
                    </p>
                    <button className="btn paynow-generate-btn" onClick={onGeneratePaynowQr} disabled={isGeneratingQr}>
                        {isGeneratingQr ?
                            <>
                                <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                Generating...
                            </>
                        :   'Generate PayNow QR Code'}
                    </button>
                </div>
            )}

            {/* Order Summary Section */}
            <div className="order-summary-card">
                <h5 className="order-summary-title">Order summary</h5>
                <div className="order-items-list">
                    {completeOrder.dishes?.map((dish, dishIndex) => {
                        const dishName = getDishDisplayName(dish)
                        const quantity = getDishQuantity(dish)
                        const dishTotal = calculateDishTotal(dish)

                        return (
                            <div key={dish.id || dishIndex} className="order-item-row">
                                <span className="order-item-name">
                                    {dishName} x {quantity}
                                </span>
                                <span className="order-item-price">$ {dishTotal.toFixed(2)}</span>
                            </div>
                        )
                    })}
                </div>
                <OrderSummaryTotals
                    originalAmount={originalAmount}
                    benefitDiscount={benefitDiscount}
                    creditDiscount={creditDiscount}
                    promoDiscount={promoDiscount}
                    finalAmount={finalAmount}
                />
            </div>
            <p className="order-placed-time">Placed at {formatPlacedTime(completeOrder.createdOn)}</p>

            {/* Footer */}
            <div className="order-complete-footer">
                <button className="btn btn-success check-status-btn" onClick={onCheckOrderStatus}>
                    Check Order Status
                </button>
            </div>
        </div>
    )
}
